// src/components/game/stages/FractureStage.jsx
import React, { useState, useRef } from 'react';
import { ASSETS } from '../../../utils/GameAssets';
import InterrogationScene from '../InterrogationScene';
import SocialBlackMarket from '../ui/SocialBlackMarket';
import ProfessionTask from '../tasks/ProfessionMechanics';

const SCENARIO_POOLS = {
  investigator: [
    { dialogue: "Why do your tariffs exceed averages by 30%?", options: [{ text: "Speed and reliability. (Truth)", integrity: 10 }, { text: "I wasn't involved. (Lie)", integrity: -20 }] },
    { dialogue: "Who authorized emergency procurement?", options: [{ text: "Executive order. (Truth)", integrity: 5 }, { text: "Paperwork lost. (Lie)", integrity: -15 }] },
    { dialogue: "Why were assets transferred before the freeze?", options: [{ text: "Asset protection. (Truth)", integrity: 0 }, { text: "No knowledge. (Lie)", integrity: -25 }] }
  ],
  commissioner: [
    { dialogue: "Why switch to fly-by-night companies?", options: [{ text: "Board recommendation. (Truth)", integrity: 10 }, { text: "Flexibility. (Lie)", integrity: -20 }] },
    { dialogue: "Who sold equipment for scrap?", options: [{ text: "CEO signed it. (Truth)", integrity: 15 }, { text: "It was damaged. (Lie)", integrity: -30 }] }
  ],
  manager: [
    { dialogue: "We are optimizing. You are incompatible.", options: [{ text: "Demand metrics. (Defense)", integrity: 10 }, { text: "Accept severance. (Submit)", integrity: -10 }] },
    { dialogue: "Legacy skills don't fit digital pivot.", options: [{ text: "Results speak. (Defense)", integrity: 20 }, { text: "I understand. (Submit)", integrity: -5 }] }
  ],
  politician: [
    { dialogue: "Foreign partners want to modernize ports.", options: [{ text: "Threat: Sovereignty.", integrity: 20 }, { text: "Accept: Upgrade.", integrity: -20 }] },
    { dialogue: "Adopt universal standards?", options: [{ text: "Threat: Bureaucracy.", integrity: 20 }, { text: "Accept: Efficiency.", integrity: -20 }] }
  ]
};

const VideoPlayer = ({ src, nextPhase }) => (
    <div className="w-full h-full bg-black flex items-center justify-center cursor-pointer" onClick={nextPhase}>
      <video src={src} autoPlay playsInline onEnded={nextPhase} className="w-full h-full object-cover" onError={nextPhase} />
      <div className="absolute bottom-10 right-10 text-xs text-gray-500 border border-gray-700 px-3 py-1 bg-black/50">Tap to Skip</div>
    </div>
);

export function FractureStage({ dispatch, profession, resources, tools, integrity }) {
  const [phase, setPhase] = useState('MARKER'); 
  const [showMarket, setShowMarket] = useState(false);
  const [qIndex, setQIndex] = useState(0); 
  
  // Chaos Roll: -60 to +60
  const fateRef = useRef(Math.floor(Math.random() * 120) - 60); 

  const renderOutcome = () => {
      const score = integrity + fateRef.current;
      let img, title, desc;

      // Logic mapping to ALL 10 Outcomes
      if (score < -40) { img = ASSETS.OUTCOMES.Prison; title = "South-Facing Cell"; desc = "Total Betrayal. The system decided you were the liability."; }
      else if (score < -20) { img = ASSETS.OUTCOMES.Summons; title = "Summoned to Carpet"; desc = "You avoided prison, but you are marked forever."; }
      else if (score < 0) { img = ASSETS.OUTCOMES.BlueWall; title = "Blue Wall of Silence"; desc = "Isolated. No one speaks to you. You are a ghost."; }
      else if (score < 20) { img = ASSETS.OUTCOMES.Hospitalized; title = "Stress Collapse"; desc = "The body broke before the mind did."; }
      else if (score < 40) { img = ASSETS.OUTCOMES.Downshifting; title = "The Grey Zone"; desc = "You survive in the shadows. Not free, but alive."; }
      else if (score < 60) { img = ASSETS.OUTCOMES.CampaignJob; title = "Riot Campaign"; desc = "You fought for your job, and won a messy war."; }
      else if (score < 80) { img = ASSETS.OUTCOMES.RevoContactor; title = "Revolution Contractor"; desc = "A contractor of chaos."; }
      else if (score < 100) { img = ASSETS.OUTCOMES.Recognition; title = "Consolation"; desc = "Luck favored you. You were given a quiet place to live out your time."; }
      else if (score < 120) { img = ASSETS.OUTCOMES.Indispensable; title = "Indispensable"; desc = "They hate you, but they need you."; }
      else { img = ASSETS.OUTCOMES.IntegrityPen; title = "The Same Pen"; desc = "You left with your honor intact. Rare."; }

      return (
          <div className="w-full h-full bg-black flex flex-col items-center justify-center relative">
              <img src={img} alt="Outcome" className="absolute inset-0 w-full h-full object-cover opacity-60" />
              <div className="relative z-10 bg-black/90 p-8 rounded-xl border-4 border-yellow-600 text-center max-w-2xl shadow-2xl">
                  <h2 className="text-4xl font-bold text-yellow-500 mb-4">{title}</h2>
                  <p className="text-white text-lg mb-6 italic font-serif">"{desc}"</p>
                  <button onClick={() => setPhase('MARKET')} className="px-10 py-4 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-full text-lg animate-pulse shadow-lg">
                      ACCESS BLACK MARKET
                  </button>
              </div>
          </div>
      );
  };

  const Scene = ({ pool, npcName, videoSrc, nextPhase }) => {
      const data = pool[qIndex]; 
      const handleChoice = (choice) => {
          dispatch({ type: 'UPDATE_STATS', payload: { integrity: choice.integrity || 0 } });
          if (qIndex + 1 < pool.length) setQIndex(prev => prev + 1);
          else { setQIndex(0); setPhase(nextPhase); }
      };

      return (
        <div className="w-full h-full relative">
            <button onClick={() => setShowMarket(!showMarket)} className="absolute top-4 right-4 z-50 bg-purple-700 text-white px-4 py-2 rounded border border-purple-500 shadow-lg">{showMarket ? "Close" : "Market"}</button>
            {showMarket && <div className="absolute inset-0 z-40 bg-black/90 p-10 overflow-y-auto"><SocialBlackMarket dispatch={dispatch} currentResources={resources} ownedTools={tools} /></div>}
            <InterrogationScene eventData={{ ...data, videoSrc, npc: npcName }} onChoiceMade={handleChoice} />
        </div>
      );
  };

  if (phase === 'MARKER') return <VideoPlayer src={ASSETS.CHAPTERS.Age1_Intro} nextPhase={() => setPhase('TEXT_1')} />;
  if (phase === 'TEXT_1') return <VideoPlayer src={ASSETS.NARRATIVE.Text1} nextPhase={() => setPhase('TEXT_2')} />;
  if (phase === 'TEXT_2') return <VideoPlayer src={ASSETS.NARRATIVE.Text2} nextPhase={() => setPhase('TEXT_3')} />;
  if (phase === 'TEXT_3') return <VideoPlayer src={ASSETS.NARRATIVE.Text3} nextPhase={() => setPhase('TEXT_4')} />;
  if (phase === 'TEXT_4') return <VideoPlayer src={ASSETS.NARRATIVE.Text4} nextPhase={() => setPhase('INV_INTRO')} />;
  if (phase === 'INV_INTRO') return <VideoPlayer src={ASSETS.NARRATIVE.InvestigatorIntro} nextPhase={() => setPhase('INVESTIGATOR')} />;
  if (phase === 'INVESTIGATOR') return <Scene pool={SCENARIO_POOLS.investigator} npcName="Investigator" videoSrc={ASSETS.NPCS.Investigator} nextPhase="COM_INTRO" />;
  if (phase === 'COM_INTRO') return <VideoPlayer src={ASSETS.NARRATIVE.CommissionerIntro} nextPhase={() => setPhase('COMMISSIONER')} />;
  if (phase === 'COMMISSIONER') return <Scene pool={SCENARIO_POOLS.commissioner} npcName="Commissioner" videoSrc={ASSETS.NPCS.Commissioner} nextPhase="PROFESSION_TASK" />;
  if (phase === 'PROFESSION_TASK') return <div className="w-full h-full bg-black flex items-center justify-center p-6"><ProfessionTask profession={profession} onComplete={(score) => { dispatch({ type: 'UPDATE_STATS', payload: { integrity: score > 50 ? 10 : -10 } }); setPhase('MANAGER'); }} /></div>;
  if (phase === 'MANAGER') return <Scene pool={SCENARIO_POOLS.manager} npcName="Manager" videoSrc={ASSETS.NPCS.Manager} nextPhase="POL_INTRO" />;
  if (phase === 'POL_INTRO') return <VideoPlayer src={ASSETS.NARRATIVE.PoliticianIntro} nextPhase={() => setPhase('POLITICIAN')} />;
  if (phase === 'POLITICIAN') return <Scene pool={SCENARIO_POOLS.politician} npcName="Politician" videoSrc={ASSETS.NPCS.Politician} nextPhase="OUTCOME" />;
  if (phase === 'OUTCOME') return renderOutcome();
  
  // New Market Phase
  if (phase === 'MARKET') {
      return <SocialBlackMarket dispatch={dispatch} currentResources={resources} ownedTools={tools} onExit={() => setPhase('TRANSITION')} />;
  }

  if (phase === 'TRANSITION') return <div className="w-full h-full bg-black flex items-center justify-center relative"><video src={ASSETS.TRANSITIONS.ToAscent} autoPlay onEnded={() => dispatch({ type: 'ADVANCE_AGE', payload: 'Ascent' })} className="w-full h-full object-cover" /></div>;

  return <div>Loading...</div>;
}